





















import { defineComponent, onUnmounted, ref } from "@vue/composition-api";
import { AgeVerificationStatus } from "@scrile/api-provider/dist/api/AgeVerificationProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useNSFWUserSettings from "@/hooks/useNSFWUserSettings";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { EVENTS } from "@/const";
import AgeVerificationSetup from "./components/AgeVerificationSetup";
import AgeVerificationStatusView from "./components/AgeVerificationStatusView";

export default defineComponent({
  name: "AgeVerification",
  components: { AgeVerificationStatusView, AgeVerificationSetup },
  setup() {
    const { authUserStrict } = useAuthUser();
    const { updateNSFWUserSettings } = useNSFWUserSettings();

    const status = ref<AgeVerificationStatus>(AgeVerificationStatus.UNKNOWN);
    const showSetup = ref(false);
    const showStatus = ref(false);
    const processing = ref(false);
    let hideStatusTimer: ReturnType<typeof setTimeout>;

    const getAgeVerification = async () => {
      const response = await providers.AgeVerificationProvider.find({ userId: authUserStrict.value.id });
      status.value = response.status;
    };

    const watchStatus = async (immediate = false) => {
      !immediate && (await new Promise(resolve => setTimeout(resolve, 3000)));
      await getAgeVerification();
      if (status.value !== AgeVerificationStatus.PENDING) {
        showStatus.value = [AgeVerificationStatus.DENIED, AgeVerificationStatus.APPROVED].includes(status.value);
        hideStatusTimer = setTimeout(() => (showStatus.value = false), 5000);
      } else {
        await watchStatus();
      }
    };

    const onEvent = async () => {
      await getAgeVerification();
      if ([AgeVerificationStatus.UNKNOWN, AgeVerificationStatus.DENIED].includes(status.value)) {
        showSetup.value = true;
      } else if (status.value === AgeVerificationStatus.PENDING) {
        showStatus.value = true;
        watchStatus();
      }
    };

    const onCancel = async () => {
      try {
        processing.value = true;
        await updateNSFWUserSettings(false);
        showSetup.value = false;
      } finally {
        processing.value = false;
      }
    };

    const onFinish = async () => {
      showSetup.value = false;
      showStatus.value = true;
      watchStatus(true);
    };

    const onRetry = async () => {
      showSetup.value = true;
    };

    const onEnableNSFW = async () => {
      try {
        hideStatusTimer && clearTimeout(hideStatusTimer);
        processing.value = true;
        await updateNSFWUserSettings(true);
        showStatus.value = false;
      } finally {
        processing.value = false;
      }
    };

    const onReset = async () => {
      const resp = await providers.AgeVerificationProvider.reset({ userId: authUserStrict.value.id });
      status.value = resp.status;
      showSetup.value = false;
    };

    emitter.on<void>(EVENTS.AGE_VERIFICATION_START, onEvent);

    onUnmounted(() => {
      emitter.off<void>(EVENTS.AGE_VERIFICATION_START, onEvent);
    });

    return {
      showSetup,
      showStatus,
      status,
      processing,
      onCancel,
      onFinish,
      onRetry,
      onEnableNSFW,
      onReset,
    };
  },
});
